// API Service for authentication and session management

export interface User {
  id: number
  email: string | null
  name: string
  handle: string
  created_at: string
  image_url: string
  image: string
  providers: Provider[]
}

export interface Provider {
  id: number
  provider_type: string
  state: string
  uid: string
  created_at: string
  updated_at: string
  crosspost_enabled: boolean | null
}

export interface Account {
  id: number
  stripe_customer_id: string | null
  subscribed: boolean
  has_deletion_plan: boolean
  has_crosspost_plan: boolean
  subscriptions: Subscription[]
}

export interface Subscription {
  id: number
  plan: string
  state: string
  stripe_customer_id: string | null
  created_at: string
  updated_at: string
}

export interface Session {
  id: string
  authenticated: boolean
  remote_ip: string
  user?: User
  account?: Account
}

export interface ApiError {
  status: string
  code: string
  detail: string
}

export interface ErrorResponse {
  errors: ApiError[]
}

export interface LoginCredentials {
  uid: string
  credentials: {
    pass: string
  }
}

// Fetch wrapper with error handling
async function apiFetch<T>(
  url: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options?.headers,
    },
    credentials: 'include', // Important for session cookies
  })

  const data = await response.json()

  if (!response.ok) {
    // Check if it's an error response with our error format
    if (data.errors && Array.isArray(data.errors)) {
      throw new Error(data.errors[0]?.detail || 'An error occurred')
    }
    throw new Error('An error occurred')
  }

  return data
}

// Get current session
export async function getCurrentSession(): Promise<Session> {
  return apiFetch<Session>('/api/v1/sessions/current')
}

// Login with Bluesky
export async function loginWithBluesky(
  uid: string,
  password: string
): Promise<User> {
  return apiFetch<User>('/api/v1/connect/bsky', {
    method: 'POST',
    body: JSON.stringify({
      connect: {
        uid,
        credentials: {
          pass: password,
        },
      },
    }),
  })
}

// Logout
export async function logout(): Promise<void> {
  return apiFetch<void>('/api/v1/secure/users/logout', {
    method: 'DELETE',
  })
}

